import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useBoardChartData } from '../../hooks/useBoardChartData';
import { BaseChartProps } from './types';
import { CHART_COLORS, CHART_STYLES, getCurrencySymbol, formatCompactNumber } from './chartTheme';
import { useSetChartMetadata } from './ChartContext';
import {
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Cell,
} from 'recharts';

const CAMPAIGN_COLORS = [
    CHART_COLORS.chart.line1,
    CHART_COLORS.chart.line2,
    CHART_COLORS.chart.line3,
    CHART_COLORS.chart.line4,
    CHART_COLORS.chart.line5,
    CHART_COLORS.chart.line6,
    CHART_COLORS.primary,
    CHART_COLORS.secondary,
    CHART_COLORS.accent,
    CHART_COLORS.success,
];

export const RevenueByUTMCampaignChart: React.FC<BaseChartProps> = () => {
    const { t } = useTranslation();
    const { finance, currencySettings } = useBoardChartData();

    const currencySymbol = useMemo(
        () => getCurrencySymbol(currencySettings?.default),
        [currencySettings?.default]
    );

    const chartData = useMemo(() => {
        if (!finance?.transactions) return [];

        const campaignRevenue: Record<string, number> = {};

        for (const tx of finance.transactions) {
            if (tx.type === 'REVENUE' && ['product_sale', 'sale'].includes(tx.category as string)) {
                const traffic = tx.traffic as Record<string, unknown> | null;
                const campaign = (traffic?.utm_campaign as string) || '';
                const label = campaign || t('charts.directNoCampaign', 'Direct / No Campaign');
                campaignRevenue[label] = (campaignRevenue[label] || 0) + (Math.abs(Number(tx.amount)) || 0);
            }
        }

        return Object.entries(campaignRevenue)
            .map(([name, revenue]) => ({ name, revenue }))
            .sort((a, b) => b.revenue - a.revenue)
            .slice(0, 10);
    }, [finance?.transactions, t]);

    const isEmpty = useMemo(() => chartData.length === 0, [chartData]);

    useSetChartMetadata({ isEmpty });

    return (
        <div className="w-full h-full bg-transparent overflow-hidden flex flex-col">
            <div className="flex-1 min-h-0 p-2">
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                        data={chartData}
                        layout="vertical"
                        margin={{ top: 10, right: 20, bottom: 10, left: 10 }}
                    >
                        <CartesianGrid
                            horizontal={false}
                            stroke={CHART_STYLES.grid.stroke}
                            strokeDasharray={CHART_STYLES.grid.strokeDasharray}
                            strokeWidth={CHART_STYLES.grid.strokeWidth}
                            opacity={CHART_STYLES.grid.opacity}
                        />
                        <XAxis
                            type="number"
                            axisLine={CHART_STYLES.axis.axisLine}
                            tickLine={CHART_STYLES.axis.tickLine}
                            tick={CHART_STYLES.axis.tick}
                            tickFormatter={(value: number) => formatCompactNumber(value, currencySymbol)}
                        />
                        <YAxis
                            type="category"
                            dataKey="name"
                            axisLine={CHART_STYLES.axis.axisLine}
                            tickLine={CHART_STYLES.axis.tickLine}
                            tick={CHART_STYLES.axis.tick}
                            width={140}
                            tickFormatter={(value: string) =>
                                value.length > 20 ? `${value.slice(0, 20)}...` : value
                            }
                        />
                        <Tooltip
                            contentStyle={CHART_STYLES.tooltip.contentStyle}
                            cursor={CHART_STYLES.tooltip.cursor}
                            formatter={(value: number) => [
                                formatCompactNumber(value, currencySymbol),
                                t('charts.revenue', 'Revenue'),
                            ]}
                        />
                        <Bar
                            dataKey="revenue"
                            radius={[0, 3, 3, 0]}
                            maxBarSize={CHART_STYLES.bar.maxBarSize}
                        >
                            {chartData.map((_entry, index) => (
                                <Cell
                                    key={`cell-${index}`}
                                    fill={CAMPAIGN_COLORS[index % CAMPAIGN_COLORS.length]}
                                />
                            ))}
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};
